import React, { useState, useEffect, useMemo } from 'react';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import Toast from 'react-bootstrap/Toast';
import ToastHeader from 'react-bootstrap/ToastHeader';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import AutocompleteDropdown from 'components/common/autocompleteDropdown/AutocompleteDropdown';
import AutocompleteMultipleDropdown from 'components/common/autocompleteMultipleDropdown/AutocompleteMultipleDropdown';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { BusinessPicker } from 'components/common/businessPicker/BusinessPicker';
import passwordValidation from 'utils/passwordValidation';
import { getAllRoles } from 'api/admin/roles/roles';
import { createNewUser } from 'api/admin/users/users';
import { useLightweightStoresInBusiness } from 'api/queries/useLightweightStoresInBusiness';
import { UserTypes } from 'constants/constants';

const hasInstallerRole = (roles) => {
  return roles.some((role) => role.display === UserTypes.INSTALLER);
};

/**
 * Determine whether new-user role selection contains a business role
 */
const hasBusinessRole = (roles) => {
  return roles.some((role) =>
    [
      UserTypes.BUSINESS_OWNER,
      UserTypes.BUSINESS_MANAGER,
      UserTypes.BUSINESS_ADMIN,
      UserTypes.EMPLOYEE,
    ].includes(role.display),
  );
};

// Material UI one-time style customization
const useStyles = makeStyles(() => ({
  inputRoot: {
    width: '65%',
  },
  formControl: {
    minWidth: '67.5%',
    maxWidth: '540px',
    paddingRight: '20px',
  },
}));

const CreateNewUser = () => {
  const [loading, setLoading] = useState();
  const [newUser, setNewUser] = useState({
    firstName: null,
    lastName: null,
    phone: null,
    email: null,
    password: null,
  });
  const [newRoles, setNewRoles] = useState([]);
  const [roles, setRoles] = useState();
  const [assignedBusinessId, setAssignedBusinessId] = useState();
  const [assignedStoreId, setAssignedStoreId] = useState();
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const materialClasses = useStyles();
  let history = useHistory();

  useEffect(() => {
    getListOfRoles();
  }, []);

  const { data: stores } = useLightweightStoresInBusiness(
    assignedBusinessId,
    hasInstallerRole(newRoles),
  );

  const storesOptions = useMemo(() => {
    return stores?.map(({ id, name }) => ({ value: id, display: name })) ?? [];
  }, [stores]);

  /**
   * Fetch a list of all available roles to assign to a new user
   */
  const getListOfRoles = async () => {
    try {
      const rolesData = await getAllRoles();
      const formattedRoles = rolesData.data.roles.map((role) => {
        return {
          display: role.userType,
          value: role.id,
        };
      });
      setRoles(formattedRoles);
    } catch (error) {
      toast.error(error.response.data.error, {
        duration: 4000,
      });
    }
  };

  /**
   * Store the incoming input value into our newUser state object
   *
   * @param {String} value
   * @param {String} field
   */
  const handleInputChange = (value, field) => {
    setNewUser({
      ...newUser,
      [field]: value,
    });
  };

  /**
   * Store the incoming role selection in the newUser state object
   *
   * @param {String} role
   */
  const handleRoleChange = (selectedItems) => {
    const installerRoleSelected = selectedItems.find(
      (role) => role.display === UserTypes.INSTALLER,
    );

    if (installerRoleSelected) {
      setNewRoles([installerRoleSelected]);
      return;
    }

    setNewRoles([...selectedItems]);
  };

  /**
   * Disable the submit button if the following is true:
   *
   * 1) newUser.firstName is empty; or
   * 2) newUser.lastName is empty; or
   * 3) newUser.phone is empty; or
   * 4) newUser.email is empty; or
   * 5) newRoles array is empty;
   */
  const disableButton = () => {
    const isInstaller = hasInstallerRole(newRoles);
    const businessAndStoreBothSelectedOrUnselected = !(
      (!assignedBusinessId && !assignedStoreId) ||
      (assignedBusinessId && assignedStoreId)
    );

    return (
      !newUser.firstName ||
      !newUser.lastName ||
      !newUser.email ||
      !newUser.phone ||
      !newUser.password ||
      newRoles.length === 0 ||
      (hasBusinessRole(newRoles) && !assignedBusinessId) ||
      (isInstaller && businessAndStoreBothSelectedOrUnselected)
    );
  };

  /**
   * Determine whether to show the "Assign to Business" form option.
   *
   * Shown when newRoles has length and includes a business or installer role.
   */
  const showBusinessOptions = useMemo(
    () => newRoles.length > 0 && (hasBusinessRole(newRoles) || hasInstallerRole(newRoles)),
    [newRoles],
  );

  /**
   * Create and store a new user in the Cents ecosystem
   */
  const createUser = async () => {
    const userPasswordValidation = passwordValidation(newUser?.password);

    if (!userPasswordValidation.result) {
      setLoading(false);
      toast.error(userPasswordValidation.errorMessage, {
        duration: 4000,
      });
      return;
    }

    try {
      setLoading(true);

      const newRolesNames = newRoles.map((role) => {
        return role.display;
      });

      const isInstaller = hasInstallerRole(newRoles);

      const data = {
        user: newUser,
        roles: newRolesNames,
        businessId: hasBusinessRole(newRoles) || isInstaller ? assignedBusinessId : null,
      };

      if (isInstaller) {
        data.storeId = assignedStoreId ?? null;
      }

      await createNewUser(data);

      setShowSuccessNotification(true);
      setLoading(false);

      navigateToUsersTable();
    } catch (error) {
      setLoading(false);
      toast.error(error.response.data.error, {
        duration: 4000,
      });
    }
  };

  /**
   * On successful user creation, navigate back to the main Users table
   */
  const navigateToUsersTable = () => {
    history.push('/users');
  };

  const onBusinessChange = (businessId) => {
    setAssignedStoreId(null);
    setAssignedBusinessId(businessId);
  };

  const onStoreChange = (event) => {
    setAssignedStoreId(event.value);
  };

  return (
    <>
      {loading && <CentsLoader />}
      {!loading && (
        <>
          {showSuccessNotification && (
            <Toast
              onClose={() => {
                setShowSuccessNotification(false);
              }}
              delay={5000}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
              }}
              autohide
            >
              <ToastHeader closeButton={true} closeLabel={'Close'}>
                New user created!
              </ToastHeader>
            </Toast>
          )}
          <div className="create-new-user-container">
            <div className="create-new-user-form-container">
              <div className="create-new-user-form">
                <h1>Create New User</h1>
                <div className="create-new-user-form-row">
                  <p className="label">First Name</p>
                  <TextField
                    variant="outlined"
                    label="First Name"
                    onChange={(event) => {
                      handleInputChange(event.target.value, 'firstName');
                    }}
                    className={materialClasses.inputRoot}
                  />
                </div>
                <div className="create-new-user-form-row">
                  <p className="label">Last Name</p>
                  <TextField
                    variant="outlined"
                    label="Last Name"
                    onChange={(event) => {
                      handleInputChange(event.target.value, 'lastName');
                    }}
                    className={materialClasses.inputRoot}
                  />
                </div>
                <div className="create-new-user-form-row">
                  <p className="label">Email</p>
                  <TextField
                    variant="outlined"
                    label="Email"
                    onChange={(event) => {
                      handleInputChange(event.target.value, 'email');
                    }}
                    className={materialClasses.inputRoot}
                  />
                </div>
                <div className="create-new-user-form-row">
                  <p className="label">Phone Number</p>
                  <TextField
                    variant="outlined"
                    label="Phone Number"
                    onChange={(event) => {
                      handleInputChange(event.target.value, 'phone');
                    }}
                    className={materialClasses.inputRoot}
                  />
                </div>
                <div className="create-new-user-form-row">
                  <p className="label">Password</p>
                  <TextField
                    variant="outlined"
                    label="Password"
                    onChange={(event) => {
                      handleInputChange(event.target.value, 'password');
                    }}
                    className={materialClasses.inputRoot}
                    type="password"
                  />
                </div>
                {roles && (
                  <div className="create-new-user-form-row">
                    <p className="label">Roles</p>
                    <FormControl variant="outlined" className={materialClasses.formControl}>
                      <AutocompleteMultipleDropdown
                        id="select-role"
                        selectedData={newRoles}
                        onChange={handleRoleChange}
                        label="Select Role(s)"
                        selectData={roles}
                      />
                    </FormControl>
                  </div>
                )}
                {showBusinessOptions && (
                  <div className="create-new-user-form-row">
                    <p className="label">Assign to Business</p>
                    <FormControl variant="outlined" className={materialClasses.formControl}>
                      <BusinessPicker
                        id="select-business"
                        selectedBusinessId={assignedBusinessId}
                        onChange={onBusinessChange}
                        label="Select a Business"
                      />
                    </FormControl>
                  </div>
                )}
                {stores?.length && hasInstallerRole(newRoles) ? (
                  <div className="create-new-user-form-row">
                    <p className="label">Assign to Store</p>
                    <FormControl variant="outlined" className={materialClasses.formControl}>
                      <AutocompleteDropdown
                        key={assignedBusinessId}
                        id="select-store"
                        selectedData={assignedStoreId}
                        onChange={onStoreChange}
                        label="Select a Store"
                        selectData={storesOptions}
                      />
                    </FormControl>
                  </div>
                ) : null}
                <div className="submit-button-container">
                  <button
                    className={'submit-button ' + (disableButton() ? 'disabled' : 'active')}
                    onClick={() => {
                      createUser();
                    }}
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CreateNewUser;
